import { useEffect, useMemo, useState } from "react";
import { getJson } from "../lib/http";
import { RefreshCw, X, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { STATIC_PRODUCTS } from "../lib/products";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PWAWelcomeReorder
 * ------------------
 * Home-page-only pill that greets a returning PWA customer with a
 * "Welcome back — reorder your last purchase?" offer.
 *
 * Visibility rules (all must be true):
 *   • The app is running in standalone display mode (installed PWA)
 *     OR the URL carries `?source=pwa` (matches the manifest's
 *     `start_url`, so browsers that don't report standalone still
 *     opt in).
 *   • `localStorage.pepura_last_order` exists — set by the
 *     EtransferConfirmation page on order load.
 *   • The pill hasn't been dismissed this session
 *     (`sessionStorage.pepura_welcome_dismissed`).
 *
 * The "Reorder" button rebuilds the customer's last cart from the
 * stored line items, re-hydrating live prices from the /products API
 * so a customer isn't offered a stale price if we've adjusted the
 * catalog since they last shopped. Then opens the cart drawer.
 */
export default function PWAWelcomeReorder() {
  const { addItem, setOpen: setCartOpen } = useCart();
  const [visible, setVisible] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [products, setProducts] = useState(STATIC_PRODUCTS);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Gate 1: standalone mode or manifest deep-link.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true ||
      new URLSearchParams(window.location.search).get("source") === "pwa";
    if (!standalone) return;
    // Gate 2: previous order stashed.
    let stored;
    try {
      stored = JSON.parse(localStorage.getItem("pepura_last_order") || "null");
    } catch {
      stored = null;
    }
    if (!stored || !Array.isArray(stored.items) || !stored.items.length) return;
    // Gate 3: not already dismissed this session.
    if (sessionStorage.getItem("pepura_welcome_dismissed") === "1") return;
    setLastOrder(stored);
    setVisible(true);
    // Refresh product list so the Reorder click sees live prices &
    // stock. Failure is silent — we fall back to STATIC_PRODUCTS.
    getJson(`${API}/products`)
      .then((data) => {
        if (data?.products?.length) setProducts(data.products);
      })
      .catch(() => {});
  }, []);

  // Human summary of what's about to be reordered ("2× Retatrutide
  // 40mg + 1× Bacteriostatic Water 10ml"). Truncated to two entries
  // for pill sanity; the "+ N more" suffix keeps the customer
  // informed without overflowing.
  const summary = useMemo(() => {
    if (!lastOrder?.items?.length) return "";
    const parts = lastOrder.items
      .slice(0, 2)
      .map((it) => `${it.qty}× ${it.name}`);
    const rest = lastOrder.items.length - 2;
    if (rest > 0) parts.push(`+${rest} more`);
    return parts.join(" · ");
  }, [lastOrder]);

  if (!visible || !lastOrder) return null;

  const dismiss = () => {
    sessionStorage.setItem("pepura_welcome_dismissed", "1");
    setVisible(false);
  };

  const reorder = () => {
    // Match stored line items against live products by id; skip any
    // SKU that no longer exists in the catalog. Live product object
    // is required because addItem() reads price/name/etc. off it.
    let added = 0;
    for (const it of lastOrder.items) {
      const live = products.find((p) => p.id === it.id);
      if (!live) continue;
      addItem(live, it.qty);
      added += 1;
    }
    dismiss();
    if (added > 0) setCartOpen(true);
  };

  return (
    <div
      data-testid="pwa-welcome-reorder"
      role="dialog"
      aria-label="Welcome back — reorder your last purchase"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[94] pointer-events-none"
    >
      <div className="pointer-events-auto rounded-xl border border-[#20a6da]/40 bg-[#0f1418]/95 shadow-xl backdrop-blur-sm p-4 text-white">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#20a6da]/15 text-[#20a6da] shrink-0">
            <ShoppingBag size={18} strokeWidth={2.25} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm">Welcome back</p>
            <p
              data-testid="pwa-welcome-summary"
              className="mt-1 text-xs text-[#bfcde3] leading-snug break-words"
            >
              Reorder your last purchase — {summary}.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                data-testid="pwa-reorder-btn"
                onClick={reorder}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#20a6da] text-[#0a0a0a] hover:bg-[#1c92bf] px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
              >
                <RefreshCw size={13} strokeWidth={2.5} />
                Reorder now
              </button>
              <button
                type="button"
                data-testid="pwa-welcome-dismiss"
                onClick={dismiss}
                className="inline-flex items-center gap-1.5 rounded-md border border-white/20 bg-transparent text-[#bfcde3] hover:text-white hover:bg-white/5 px-3 py-1.5 text-xs font-medium uppercase tracking-wide"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close welcome"
            data-testid="pwa-welcome-close"
            onClick={dismiss}
            className="inline-flex items-center justify-center h-6 w-6 rounded text-[#bfcde3] hover:text-white hover:bg-white/10 shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
